import { useState, useEffect, useMemo, useRef } from "react";
import { useApp } from "../context/AppContext";
import { Icons } from "../components/Icons";

const STATUS_COLORS = { keep: "#10b981", blog: "#8b5cf6", review: "#f59e0b", reject: "#ef4444", new_page: "#6366f1" };
const STATUS_LABELS = { keep: "Залишити", blog: "Блог", review: "Перевірити", reject: "Відхилити", new_page: "Нова стор." };
const INTENT_LABELS = { commercial: "Комерц.", informational: "Інформ.", navigational: "Навігац.", mixed: "Змішаний" };

const TYPE_LABELS = { guide: "Гайд", review: "Огляд", comparison: "Порівняння", top: "ТОП", tips: "Поради", faq: "FAQ" };
const TYPE_COLORS = { guide: "#3b82f6", review: "#10b981", comparison: "#f59e0b", top: "#ef4444", tips: "#8b5cf6", faq: "#06b6d4" };

export default function SemanticCleanerView() {
  const { proj, activeId, apiFetch, dark, t, card, inp, btnP, btnG, btnSm, flash, bgTasks, loadProjectData } = useApp();
  const [mainTab, setMainTab] = useState("keywords"); // "keywords" | "blog"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blogStructure, setBlogStructure] = useState(null);
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogExpanded, setBlogExpanded] = useState(new Set());
  const [collecting, setCollecting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [tab, setTab] = useState("keep");
  const [search, setSearch] = useState("");
  const [pageFilter, setPageFilter] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [sortCol, setSortCol] = useState("frequency");
  const [sortDir, setSortDir] = useState("desc");
  const [collectLang, setCollectLang] = useState(null); // null = dialog not shown
  const [collectPages, setCollectPages] = useState([]); // available pages
  const [checkedPages, setCheckedPages] = useState(new Set());
  const [collectStep, setCollectStep] = useState(null); // null | "pages" | "mode" | "lang"
  const [collectMode, setCollectMode] = useState("name"); // "url" | "name" | "name_domain" | "domain"
  const [collectNiche, setCollectNiche] = useState(""); // e.g. "косметика купити"
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pageSearch, setPageSearch] = useState("");
  const PP = 50;
  const token = localStorage.getItem("metago_token");

  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    apiFetch(`/api/projects/${activeId}/semantic-cleaner`).then(r => r.json())
      .then(d => { if (d) { setData(d); if (d.blogStructure) setBlogStructure(d.blogStructure); } })
      .catch(() => {}).finally(() => setLoading(false));
  }, [activeId, apiFetch]);

  const generateBlogStructure = async () => {
    setBlogLoading(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/semantic-cleaner/blog-structure`, { method: "POST" });
      const d = await r.json();
      if (d.error) { flash(d.error); } else { setBlogStructure(d); flash("Структуру блогу згенеровано!"); }
    } catch (e) { flash("Помилка: " + e.message); }
    setBlogLoading(false);
  };

  const openCollectModal = async () => {
    setPagesLoading(true);
    setCollectStep("pages");
    setPageSearch("");
    try {
      const r = await apiFetch(`/api/projects/${activeId}/semantic-cleaner/pages`);
      const pages = await r.json();
      setCollectPages(Array.isArray(pages) ? pages : []);
      setCheckedPages(new Set(pages.map(p => p.url)));
    } catch { setCollectPages([]); }
    setPagesLoading(false);
  };

  const collect = async (lang) => {
    setCollectStep(null);
    setCollectLang(null);
    setCollecting(true);
    setProgress({ step: "Запуск...", current: 0, total: 1 });
    try {
      const selectedPages = [...checkedPages];
      const r = await apiFetch(`/api/projects/${activeId}/semantic-cleaner/collect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lang: lang || "all", selectedPages, mode: collectMode, niche: collectNiche }),
      });
      const d = await r.json();
      if (d.ok) {
        flash(`Збір семантики запущено (${selectedPages.length} сторінок). Можна закрити сторінку.`);
      } else {
        flash(d.error || "Error");
        setCollecting(false);
        setProgress(null);
      }
    } catch (e) { flash("Помилка: " + e.message); setCollecting(false); setProgress(null); }
  };

  // Poll for background task progress via notifications
  useEffect(() => {
    const task = bgTasks.find(t => t.projectId === activeId && t.type === "semantic_collect" && t.status === "running");
    if (task) {
      setCollecting(true);
      setProgress(task.progress || { step: "Обробка...", current: 0, total: 1 });
    } else if (collecting) {
      // Task finished — reload data
      setCollecting(false);
      setProgress(null);
      apiFetch(`/api/projects/${activeId}/semantic-cleaner`).then(r => r.json()).then(d => { if (d) setData(d); }).catch(() => {});
    }
  }, [bgTasks, activeId]);

  const approve = async () => {
    if (!confirm("Утвердити семантику? Keep-ключі будуть додані в кластери проекту.")) return;
    try {
      const r = await apiFetch(`/api/projects/${activeId}/semantic-cleaner/approve`, { method: "POST" });
      const d = await r.json();
      if (d.ok) {
        flash(`Утверджено: ${d.clusters} кластерів, ${d.keywords} ключів`);
        await loadProjectData(activeId);
      } else flash(d.error);
    } catch (e) { flash("Помилка: " + e.message); }
  };

  // Page name lookup from structure
  const pageNameMap = useMemo(() => {
    const map = {};
    if (data?.pages) data.pages.forEach(p => { map[p.url] = p.name; map[p.url.replace(/\/+$/, "")] = p.name; });
    return map;
  }, [data]);

  // All keywords flat list
  const allKeywords = useMemo(() => {
    if (!data?.assignments) return [];
    const kws = [];
    Object.entries(data.assignments).forEach(([url, items]) => {
      items.forEach(k => kws.push({ ...k, url, pageName: pageNameMap[url] || pageNameMap[url.replace(/\/+$/, "")] || "" }));
    });
    if (data.unassigned) kws.push(...data.unassigned);
    return kws;
  }, [data, pageNameMap]);

  // Filtered + sorted
  const filtered = useMemo(() => {
    let items = allKeywords.filter(k => k.status === tab);
    if (search) {
      const sq = search.toLowerCase();
      items = items.filter(k => k.keyword.toLowerCase().includes(sq) || (k.url || "").toLowerCase().includes(sq));
    }
    if (pageFilter) items = items.filter(k => k.url === pageFilter);
    items.sort((a, b) => {
      const av = sortCol === "keyword" ? (a.keyword || "") : (a[sortCol] || 0);
      const bv = sortCol === "keyword" ? (b.keyword || "") : (b[sortCol] || 0);
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return items;
  }, [allKeywords, tab, search, pageFilter, sortCol, sortDir]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir(col === "keyword" ? "asc" : "desc"); }
  };

  const paged = filtered.slice(page * PP, (page + 1) * PP);
  const totalPages = Math.ceil(filtered.length / PP);

  // Save assignments to DB (debounced)
  const saveRef = useRef(null);
  const saveAssignments = (assignments) => {
    if (saveRef.current) clearTimeout(saveRef.current);
    saveRef.current = setTimeout(() => {
      apiFetch(`/api/projects/${activeId}/semantic-cleaner`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assignments }),
      }).catch(() => {});
    }, 800);
  };

  // Change status locally + save
  const changeStatus = (keyword, newStatus) => {
    if (!data) return;
    const next = { ...data, assignments: { ...data.assignments } };
    Object.keys(next.assignments).forEach(url => {
      next.assignments[url] = next.assignments[url].map(k => k.normalized === keyword ? { ...k, status: newStatus } : k);
    });
    setData(next);
    saveAssignments(next.assignments);
  };

  // Bulk change status + save
  const bulkChangeStatus = (newStatus) => {
    if (!data || !selected.size) return;
    const next = { ...data, assignments: { ...data.assignments } };
    Object.keys(next.assignments).forEach(url => {
      next.assignments[url] = next.assignments[url].map(k => selected.has(k.normalized) ? { ...k, status: newStatus } : k);
    });
    setData(next);
    saveAssignments(next.assignments);
    flash(`${selected.size} ключів → ${STATUS_LABELS[newStatus]}`);
    setSelected(new Set());
  };

  const toggleSelect = (norm) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(norm) ? next.delete(norm) : next.add(norm);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(k => k.normalized)));
  };

  const tabStyle = (active) => ({
    padding: "8px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer", border: "none",
    borderBottom: active ? `2px solid ${STATUS_COLORS[tab]}` : "2px solid transparent",
    background: "none", color: active ? STATUS_COLORS[tab] || t.text : t.textMuted, fontFamily: "inherit",
  });

  if (!proj) return null;
  const s = data?.stats || {};

  // Language dialog
  const LANGS = [
    { code: "all", label: "Всі мови", desc: "Збирає ключі на всіх мовах (uk, ru, en)" },
    { code: "uk", label: "Українська", desc: "Тільки українськомовні запити" },
    { code: "ru", label: "Російська", desc: "Тільки російськомовні запити" },
    { code: "en", label: "Англійська", desc: "Тільки англомовні запити" },
    { code: "uk,ru", label: "Укр + Рос", desc: "Українські та російські запити" },
  ];

  return (
    <>
    {collectStep && (
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
        onClick={() => setCollectStep(null)}>
        <div onClick={e => e.stopPropagation()} style={{ background: dark ? "#1a1a2e" : "#fff", borderRadius: 16, padding: 24, maxWidth: 520, width: "90%", maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>

          {collectStep === "pages" && (
            <>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Оберіть сторінки для збору</div>
              <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 12 }}>
                Відмітьте сторінки, для яких потрібно зібрати семантику
              </div>
              {pagesLoading ? (
                <div style={{ padding: 40, textAlign: "center", color: t.textMuted }}>Завантаження...</div>
              ) : (
                <>
                  <input
                    value={pageSearch} onChange={e => setPageSearch(e.target.value)}
                    placeholder="Пошук сторінок..."
                    style={{ ...inp, marginBottom: 8, fontSize: 13 }}
                  />
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <button style={{ ...btnSm, fontSize: 11 }} onClick={() => {
                      const visible = collectPages.filter(p => !pageSearch || p.name.toLowerCase().includes(pageSearch.toLowerCase()) || p.url.toLowerCase().includes(pageSearch.toLowerCase()));
                      setCheckedPages(prev => {
                        const next = new Set(prev);
                        const allChecked = visible.every(p => next.has(p.url));
                        visible.forEach(p => allChecked ? next.delete(p.url) : next.add(p.url));
                        return next;
                      });
                    }}>
                      {(() => { const visible = collectPages.filter(p => !pageSearch || p.name.toLowerCase().includes(pageSearch.toLowerCase()) || p.url.toLowerCase().includes(pageSearch.toLowerCase())); return visible.every(p => checkedPages.has(p.url)) ? "Зняти всі" : "Обрати всі"; })()}
                    </button>
                    <span style={{ fontSize: 11, color: t.textMuted, lineHeight: "28px" }}>Обрано: {checkedPages.size} / {collectPages.length}</span>
                  </div>
                  <div style={{ flex: 1, overflowY: "auto", maxHeight: "45vh", border: `1px solid ${t.border}`, borderRadius: 10 }}>
                    {collectPages
                      .filter(p => !pageSearch || p.name.toLowerCase().includes(pageSearch.toLowerCase()) || p.url.toLowerCase().includes(pageSearch.toLowerCase()))
                      .map(p => (
                      <label key={p.url} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`, cursor: "pointer", fontSize: 13 }}
                        onMouseEnter={e => e.currentTarget.style.background = dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <input type="checkbox" checked={checkedPages.has(p.url)} onChange={() => {
                          setCheckedPages(prev => {
                            const next = new Set(prev);
                            next.has(p.url) ? next.delete(p.url) : next.add(p.url);
                            return next;
                          });
                        }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500 }}>{p.name}</div>
                          <div style={{ fontSize: 10, color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.url}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button onClick={() => { if (checkedPages.size === 0) { flash("Оберіть хоча б одну сторінку"); return; } setCollectStep("mode"); }} style={{ ...btnP, flex: 1 }} disabled={checkedPages.size === 0}>
                  Далі → Режим ({checkedPages.size} стор.)
                </button>
                <button onClick={() => setCollectStep(null)} style={{ ...btnG, flex: 0 }}>Скасувати</button>
              </div>
            </>
          )}

          {collectStep === "mode" && (
            <>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Режим збору</div>
              <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 12 }}>
                Як шукати ключові слова в Serpstat?
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                {[
                  { code: "name", label: "По назві сторінки", desc: "Шукає за назвою категорії, наприклад «Креми для обличчя»" },
                  { code: "name_domain", label: "Назва + домен", desc: "Шукає «Креми для обличчя infarma.com.ua» — точніше для відомих сайтів" },
                  { code: "url", label: "По URL сторінки", desc: "Шукає ключі, по яких URL вже ранжується (потрібен живий сайт)" },
                ].map(m => (
                  <div key={m.code} onClick={() => setCollectMode(m.code)} style={{
                    padding: "12px 16px", borderRadius: 10, cursor: "pointer",
                    border: `1px solid ${collectMode === m.code ? "#6366f1" : t.border}`,
                    background: collectMode === m.code ? (dark ? "rgba(99,102,241,0.1)" : "rgba(99,102,241,0.05)") : (dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"),
                    transition: "all .15s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "#6366f1"}
                    onMouseLeave={e => { if (collectMode !== m.code) e.currentTarget.style.borderColor = t.border; }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{m.label}</div>
                    <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{m.desc}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Ніша / контекст сайту</div>
                <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 6 }}>
                  Додає контекст до пошуку та фільтрує нерелевантні ключі. Наприклад: «косметика купити догляд»
                </div>
                <input
                  value={collectNiche} onChange={e => setCollectNiche(e.target.value)}
                  placeholder="косметика купити догляд ціна"
                  style={{ ...inp, fontSize: 13, width: "100%" }}
                />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setCollectStep("lang")} style={{ ...btnP, flex: 1 }}>Далі → Мова</button>
                <button onClick={() => setCollectStep("pages")} style={{ ...btnG, flex: 0 }}>← Назад</button>
                <button onClick={() => setCollectStep(null)} style={{ ...btnG, flex: 0 }}>Скасувати</button>
              </div>
            </>
          )}

          {collectStep === "lang" && (
            <>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Мова збору семантики</div>
              <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 16 }}>
                {checkedPages.size} сторінок обрано. Оберіть мову для збору ключових слів.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {LANGS.map(l => (
                  <div key={l.code} onClick={() => collect(l.code)} style={{
                    padding: "12px 16px", borderRadius: 10, cursor: "pointer",
                    border: `1px solid ${t.border}`, background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                    transition: "all .15s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "#6366f1"}
                    onMouseLeave={e => e.currentTarget.style.borderColor = t.border}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{l.label}</div>
                    <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{l.desc}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button onClick={() => setCollectStep("mode")} style={{ ...btnG, flex: 1 }}>← Назад</button>
                <button onClick={() => setCollectStep(null)} style={{ ...btnG, flex: 0 }}>Скасувати</button>
              </div>
            </>
          )}

        </div>
      </div>
    )}
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Збір семантики</h2>
        <div style={{ display: "flex", gap: 4, marginLeft: 16 }}>
          {[
            { key: "keywords", label: "Ключові слова", icon: Icons.key(14) },
            { key: "blog", label: "Контент-план", icon: Icons.edit(14) },
          ].map(mt => (
            <button key={mt.key} onClick={() => setMainTab(mt.key)} style={{
              padding: "6px 16px", borderRadius: 8, border: "none", cursor: "pointer",
              background: mainTab === mt.key ? "#6366f1" : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"),
              color: mainTab === mt.key ? "#fff" : t.textMuted,
              fontWeight: mainTab === mt.key ? 700 : 500, fontSize: 12, fontFamily: "inherit",
              display: "flex", alignItems: "center", gap: 5,
            }}>
              {mt.icon} {mt.label}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {mainTab === "keywords" && data && <button onClick={approve} style={{ ...btnG, fontSize: 12, color: "#10b981", borderColor: "#10b98133" }}>Утвердити семантику</button>}
          {mainTab === "keywords" && <button onClick={() => openCollectModal()} disabled={collecting} style={{ ...btnP, fontSize: 12, opacity: collecting ? 0.6 : 1 }}>
            {collecting ? "Збираю..." : "Зібрати семантику"}
          </button>}
          {mainTab === "blog" && <button onClick={generateBlogStructure} disabled={blogLoading} style={{ ...btnP, fontSize: 12, opacity: blogLoading ? 0.6 : 1 }}>
            {blogLoading ? <>{Icons.clock(14)} Генерую...</> : <>{Icons.robot(16)} Згенерувати контент-план</>}
          </button>}
        </div>
      </div>

      {/* ═══ BLOG TAB ═══ */}
      {mainTab === "blog" && (
        <div>
          {blogLoading ? (
            <div style={{ ...card, textAlign: "center", padding: 60, color: t.textMuted }}>
              <div style={{ marginBottom: 12 }}>{Icons.robot(16)}</div>
              <div style={{ fontWeight: 600 }}>Генерую контент-план...</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>AI аналізує зібрані ключі та створює структуру блогу</div>
            </div>
          ) : !blogStructure ? (
            <div style={{ ...card, textAlign: "center", padding: 60 }}>
              <div style={{ marginBottom: 12 }}>{Icons.edit(16)}</div>
              <p style={{ fontWeight: 600, marginBottom: 8 }}>Контент-план блогу</p>
              <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 16, maxWidth: 480, margin: "0 auto 16px" }}>
                AI створить структуру блогу на основі зібраних інформаційних ключів: рубрики, теми статей, цільові ключі та типи контенту.
              </p>
              <button onClick={generateBlogStructure} style={btnP}>{Icons.robot(16)} Згенерувати контент-план</button>
            </div>
          ) : (
            <>
              {/* Blog stats */}
              <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                <div style={{ ...card, flex: 1, minWidth: 120, padding: 12, marginBottom: 0, textAlign: "center" }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "#8b5cf6" }}>{blogStructure.sections?.length || 0}</div>
                  <div style={{ fontSize: 9, color: t.textMuted, fontWeight: 600, textTransform: "uppercase" }}>Рубрик</div>
                </div>
                <div style={{ ...card, flex: 1, minWidth: 120, padding: 12, marginBottom: 0, textAlign: "center" }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "#10b981" }}>{blogStructure.totalTopics || 0}</div>
                  <div style={{ fontSize: 9, color: t.textMuted, fontWeight: 600, textTransform: "uppercase" }}>Тем</div>
                </div>
                <div style={{ ...card, flex: 1, minWidth: 120, padding: 12, marginBottom: 0, textAlign: "center" }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "#f59e0b" }}>{blogStructure.sections?.reduce((s, sec) => s + (sec.topics || []).reduce((ts, tp) => ts + (tp.frequency || 0), 0), 0)}</div>
                  <div style={{ fontSize: 9, color: t.textMuted, fontWeight: 600, textTransform: "uppercase" }}>Сумарна частотність</div>
                </div>
                {blogStructure.generatedAt && (
                  <div style={{ ...card, flex: 1, minWidth: 120, padding: 12, marginBottom: 0, textAlign: "center" }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: t.textMuted }}>{blogStructure.generatedAt.slice(0, 10)}</div>
                    <div style={{ fontSize: 9, color: t.textMuted, fontWeight: 600, textTransform: "uppercase" }}>Згенеровано</div>
                  </div>
                )}
              </div>

              {/* Sections */}
              {(blogStructure.sections || []).map((sec, si) => {
                const isOpen = blogExpanded.has(si);
                const typeStats = {};
                (sec.topics || []).forEach(tp => { typeStats[tp.type] = (typeStats[tp.type] || 0) + 1; });
                return (
                  <div key={si} style={{ ...card, marginBottom: 10, overflow: "hidden" }}>
                    <div onClick={() => setBlogExpanded(prev => {
                      const next = new Set(prev);
                      next.has(si) ? next.delete(si) : next.add(si);
                      return next;
                    })} style={{ padding: "16px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 28 }}>{sec.icon || Icons.filter(16)}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 2 }}>{sec.name}</div>
                        <div style={{ fontSize: 11, color: t.textMuted }}>{sec.description}</div>
                        <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                          {sec.linkedCategory && (
                            <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 6, background: dark ? "rgba(99,102,241,0.1)" : "rgba(99,102,241,0.06)", color: "#6366f1", fontWeight: 600 }}>
                              → {sec.linkedCategory}
                            </span>
                          )}
                          {Object.entries(typeStats).map(([type, count]) => (
                            <span key={type} style={{ fontSize: 9, padding: "2px 8px", borderRadius: 6, background: `${TYPE_COLORS[type] || "#888"}15`, color: TYPE_COLORS[type] || "#888", fontWeight: 600 }}>
                              {TYPE_LABELS[type] || type}: {count}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: "#8b5cf6" }}>{(sec.topics || []).length}</div>
                        <div style={{ fontSize: 9, color: t.textMuted }}>тем</div>
                      </div>
                      <span style={{ fontSize: 12, color: t.textMuted, transition: "transform .2s", transform: isOpen ? "rotate(180deg)" : "rotate(0)" }}>▼</span>
                    </div>
                    {isOpen && (
                      <div style={{ borderTop: `1px solid ${t.border}` }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr>
                              <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", borderBottom: `1px solid ${t.border}` }}>Тема статті (H1)</th>
                              <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", borderBottom: `1px solid ${t.border}`, width: 90 }}>Частотн.</th>
                              <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", borderBottom: `1px solid ${t.border}`, width: 90 }}>Тип</th>
                              <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", borderBottom: `1px solid ${t.border}` }}>Опис</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(sec.topics || []).map((tp, ti) => (
                              <tr key={ti} style={{ background: ti % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)") }}>
                                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 500, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                                  {tp.title}
                                  {tp.targetKeyword && <div style={{ fontSize: 10, color: "#6366f1", marginTop: 2, display: "flex", alignItems: "center", gap: 3 }}>{Icons.key(14)} {tp.targetKeyword}</div>}
                                  {tp.slug && <div style={{ fontSize: 9, color: t.textMuted, marginTop: 1 }}>/blog/{tp.slug}/</div>}
                                </td>
                                <td style={{ padding: "10px 12px", textAlign: "center", fontSize: 12, fontWeight: 700, color: tp.frequency > 100 ? "#10b981" : tp.frequency > 30 ? "#f59e0b" : t.textMuted, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                                  {tp.frequency || "—"}
                                </td>
                                <td style={{ padding: "10px 12px", textAlign: "center", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                                  <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 6, background: `${TYPE_COLORS[tp.type] || "#888"}15`, color: TYPE_COLORS[tp.type] || "#888", fontWeight: 700 }}>
                                    {TYPE_LABELS[tp.type] || tp.type}
                                  </span>
                                </td>
                                <td style={{ padding: "10px 12px", fontSize: 11, color: t.textMuted, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                                  {tp.description}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* ═══ KEYWORDS TAB ═══ */}
      {mainTab === "keywords" && <>
      {/* Progress */}
      {collecting && progress && (
        <div style={{ ...card, padding: "14px 16px", marginBottom: 12, border: `1px solid ${dark ? "rgba(99,102,241,0.2)" : "rgba(99,102,241,0.1)"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{progress.step}</span>
            <span style={{ fontSize: 12, color: t.textMuted, marginLeft: "auto" }}>
              {progress.current}/{progress.total}
              {progress.total > 0 && <span style={{ marginLeft: 6, fontWeight: 700, color: "#6366f1" }}>{Math.round((progress.current / progress.total) * 100)}%</span>}
            </span>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: dark ? "#252535" : "#e5e7eb", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%`, background: "linear-gradient(90deg, #6366f1, #8b5cf6, #10b981)", borderRadius: 4, transition: "width .5s" }} />
          </div>
          <div style={{ fontSize: 10, color: t.textMuted, marginTop: 4 }}>
            Сторінка {progress.current} з {progress.total}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ ...card, textAlign: "center", padding: 40, color: t.textMuted }}>Завантаження...</div>
      ) : !data ? (
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{ marginBottom: 12 }}>{Icons.filter(16)}</div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Автоматичний збір семантики</p>
          <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 16, maxWidth: 480, margin: "0 auto 16px" }}>
            Система збере ключі з GSC + Serpstat (домен + синоніми + розширення), очистить від мусору, розподілить по сторінках структури і підготує для перевірки.
          </p>
          <button onClick={() => openCollectModal()} disabled={collecting} style={btnP}>{collecting ? progress?.step : "Зібрати семантику"}</button>
        </div>
      ) : (
        <>
          {/* Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8, marginBottom: 14 }}>
            {[
              { label: "Зібрано", value: s.totalRaw || 0, color: "#6366f1" },
              { label: "Унікальних", value: s.totalUnique || 0, color: "#94a3b8" },
              { label: "Залишити", value: s.totalKeep || 0, color: "#10b981" },
              { label: "Блог", value: s.totalBlog || 0, color: "#8b5cf6" },
              { label: "Перевірити", value: s.totalReview || 0, color: "#f59e0b" },
              { label: "Відхилити", value: s.totalReject || 0, color: "#ef4444" },
            ].map((st, i) => (
              <div key={i} style={{ ...card, marginBottom: 0, padding: 12, textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: st.color }}>{st.value}</div>
                <div style={{ fontSize: 9, color: t.textMuted, fontWeight: 600, textTransform: "uppercase" }}>{st.label}</div>
              </div>
            ))}
          </div>

          {/* Sources */}
          {s.sources && (
            <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
              {Object.entries(s.sources).filter(([, v]) => v > 0).map(([k, v]) => (
                <span key={k} style={{ fontSize: 10, padding: "3px 8px", borderRadius: 6, background: dark ? "#252535" : "#f3f4f6", color: t.textMuted }}>
                  {k.replace("serpstat_", "").replace("_", " ")}: {v}
                </span>
              ))}
              <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 6, background: dark ? "#252535" : "#f3f4f6", color: t.textMuted }}>
                {s.pagesWithKeywords}/{s.totalPages} сторінок
              </span>
              {data.collectedAt && <span style={{ fontSize: 10, color: t.textMuted, marginLeft: "auto" }}>{data.collectedAt.slice(0, 16).replace("T", " ")}</span>}
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: "flex", borderBottom: `1px solid ${t.border}`, marginBottom: 10 }}>
            {["keep", "blog", "review", "reject"].map(st => (
              <button key={st} style={tabStyle(tab === st)} onClick={() => { setTab(st); setPage(0); }}>
                {STATUS_LABELS[st]} ({allKeywords.filter(k => k.status === st).length})
              </button>
            ))}
          </div>

          {/* Filters */}
          <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} placeholder="Пошук ключа..." style={{ ...inp, flex: 1, fontSize: 12, padding: "6px 10px" }} />
            <select value={pageFilter} onChange={e => { setPageFilter(e.target.value); setPage(0); }} style={{ ...inp, width: 250, fontSize: 11, padding: "6px 8px" }}>
              <option value="">Всі сторінки</option>
              {(data.pages || []).filter(p => p.keywordCount > 0).map(p => (
                <option key={p.url} value={p.url}>{p.name} ({p.keywordCount})</option>
              ))}
            </select>
          </div>

          {/* Bulk actions */}
          {selected.size > 0 && (
            <div style={{ ...card, padding: "8px 14px", marginBottom: 6, display: "flex", alignItems: "center", gap: 8, background: dark ? "rgba(99,102,241,0.06)" : "rgba(99,102,241,0.03)", border: `1px solid ${dark ? "rgba(99,102,241,0.2)" : "rgba(99,102,241,0.1)"}` }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#6366f1" }}>{selected.size} обрано</span>
              <button onClick={() => bulkChangeStatus("keep")} style={{ ...btnSm, background: "rgba(16,185,129,0.1)", color: "#10b981", border: "1px solid #10b98133" }}>Залишити</button>
              <button onClick={() => bulkChangeStatus("review")} style={{ ...btnSm, background: "rgba(245,158,11,0.1)", color: "#f59e0b", border: "1px solid #f59e0b33" }}>На перевірку</button>
              <button onClick={() => bulkChangeStatus("reject")} style={{ ...btnSm, background: "rgba(239,68,68,0.1)", color: "#ef4444", border: "1px solid #ef444433" }}>Відхилити</button>
              <button onClick={() => setSelected(new Set())} style={{ ...btnSm, marginLeft: "auto" }}>Скинути</button>
            </div>
          )}

          {/* Table */}
          <div style={{ ...card, padding: 0, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ padding: "8px 4px", textAlign: "center", borderBottom: `2px solid ${t.border}`, width: 30 }}>
                    <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={selectAll} style={{ cursor: "pointer" }} />
                  </th>
                  {[
                    { label: "Ключ", col: "keyword", align: "left" },
                    { label: "Частотн.", col: "frequency", align: "center" },
                    { label: "Score", col: "score", align: "center" },
                    { label: "Інтент", col: null, align: "center" },
                    { label: "Джерело", col: null, align: "center" },
                    { label: "Сторінка", col: null, align: "left" },
                    { label: "Статус", col: null, align: "center" },
                  ].map(h => (
                    <th key={h.label} onClick={h.col ? () => toggleSort(h.col) : undefined} style={{
                      padding: "8px 8px", textAlign: h.align, fontSize: 10, fontWeight: 700,
                      color: sortCol === h.col ? "#6366f1" : t.textMuted,
                      borderBottom: `2px solid ${t.border}`, textTransform: "uppercase",
                      cursor: h.col ? "pointer" : "default", userSelect: "none",
                    }}>
                      {h.label}{sortCol === h.col ? (sortDir === "desc" ? " ↓" : " ↑") : ""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.map((k, i) => (
                  <tr key={i} style={{ background: selected.has(k.normalized) ? (dark ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.04)") : i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)") }}>
                    <td style={{ padding: "6px 4px", textAlign: "center", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                      <input type="checkbox" checked={selected.has(k.normalized)} onChange={() => toggleSelect(k.normalized)} style={{ cursor: "pointer" }} />
                    </td>
                    <td style={{ padding: "6px 8px", fontSize: 12, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                      {k.keyword}
                      {k.position && <span style={{ fontSize: 9, color: "#6366f1", marginLeft: 4 }}>#{k.position}</span>}
                    </td>
                    <td style={{ padding: "6px 8px", textAlign: "center", fontSize: 11, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}`, color: t.textMuted }}>{k.frequency || 0}</td>
                    <td style={{ padding: "6px 8px", textAlign: "center", fontSize: 11, fontWeight: 700, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}`, color: k.score >= 5 ? "#10b981" : k.score >= 1 ? "#f59e0b" : "#ef4444" }}>{k.score}</td>
                    <td style={{ padding: "6px 8px", textAlign: "center", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                      <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: dark ? "#252535" : "#f3f4f6", color: t.textMuted }}>{INTENT_LABELS[k.intent] || k.intent}</span>
                    </td>
                    <td style={{ padding: "6px 8px", textAlign: "center", fontSize: 9, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}`, color: t.textMuted }}>{k.sources}</td>
                    <td style={{ padding: "6px 8px", fontSize: 11, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}`, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: t.textMuted }} title={k.url}>
                      {k.pageName || (k.url || "").replace(/^https?:\/\/[^/]+/, "")}
                    </td>
                    <td style={{ padding: "6px 8px", textAlign: "center", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                      <select value={k.status} onChange={e => changeStatus(k.normalized, e.target.value)} style={{ fontSize: 10, padding: "2px 4px", borderRadius: 4, border: `1px solid ${STATUS_COLORS[k.status]}33`, background: `${STATUS_COLORS[k.status]}15`, color: STATUS_COLORS[k.status], fontWeight: 700, cursor: "pointer" }}>
                        {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", gap: 8, padding: "12px", borderTop: `1px solid ${t.border}`, alignItems: "center" }}>
                <button disabled={page === 0} onClick={() => setPage(p => p - 1)} style={{ ...btnSm, opacity: page === 0 ? 0.4 : 1 }}>‹</button>
                <span style={{ fontSize: 12, color: t.textMuted }}>{page * PP + 1}–{Math.min((page + 1) * PP, filtered.length)} з {filtered.length}</span>
                <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} style={{ ...btnSm, opacity: page >= totalPages - 1 ? 0.4 : 1 }}>›</button>
              </div>
            )}
          </div>
        </>
      )}
      </>}
    </div>
    </>
  );
}
